import { type ReactNode } from "react";
import { cn } from "@shared/lib/cn";
import { Button, type ButtonProps } from "@shared/ui/primitives/Button";

/**
 * Section wrapper with eyebrow label + optional trailing action. Used to
 * head every grouped block on dashboards, settings, and detail screens.
 */
interface SectionProps {
  title: string;
  action?: ReactNode;
  description?: string;
  children: ReactNode;
  className?: string;
}

export function Section({
  title,
  action,
  description,
  children,
  className,
}: SectionProps): JSX.Element {
  return (
    <section className={cn("mt-8 first:mt-0", className)}>
      <div className="mb-2 flex items-baseline justify-between gap-3 px-0">
        <span className="eyebrow">{title}</span>
        {action ? (
          <div className="text-caption text-primary-700">{action}</div>
        ) : null}
      </div>
      {description ? (
        <p className="text-body-sm text-ink-secondary mb-3">{description}</p>
      ) : null}
      {children}
    </section>
  );
}

export function SectionActionButton({
  className,
  variant = "link",
  size = "sm",
  ...props
}: ButtonProps): JSX.Element {
  return (
    <Button
      variant={variant}
      size={size}
      className={cn(
        "h-auto gap-1 px-0 py-0 text-caption font-semibold no-underline hover:no-underline",
        className,
      )}
      {...props}
    />
  );
}
